import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { DashboardSummary } from '../types'
import { daysBetween, fmtDate, fmtMoney } from '../lib/format'
import ReadyToDigCard from '../components/ReadyToDigCard'

type WeatherData = Awaited<ReturnType<typeof api.weather.get>>

export default function TodayTab() {
  const [data, setData] = useState<DashboardSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [weather, setWeather] = useState<Record<string, WeatherData>>({})

  useEffect(() => {
    api
      .dashboard()
      .then(async (d) => {
        setData(d)
        // Fetch cached weather for each active project (no-op if not geocoded)
        for (const p of d.active_projects) {
          try {
            const w = await api.weather.get(p.id)
            setWeather((prev) => ({ ...prev, [p.id]: w }))
          } catch {
            /* skip — project not geocoded yet */
          }
        }
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className="muted">Loading…</p>
  if (err)
    return (
      <div className="card alert">
        <strong>Couldn't load dashboard:</strong> {err}
      </div>
    )
  if (!data) return null

  const totalAttention =
    data.overdue_decisions.length +
    data.overdue_selections.length +
    data.pending_change_orders.length +
    data.expiring_cois.length +
    data.open_warranty_claims.filter((c) => c.is_emergency === 1).length +
    data.upcoming_compliance.length

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Today</h2>
          <p className="muted small">
            {fmtDate(data.today)} · {data.active_projects.length} active project
            {data.active_projects.length === 1 ? '' : 's'} · {totalAttention} item
            {totalAttention === 1 ? '' : 's'} need attention
          </p>
        </div>
      </div>

      {/* "Ready to dig" composite per active project */}
      {data.active_projects.map((p) => (
        <ReadyToDigCard key={p.id} project={p as any} />
      ))}

      {totalAttention === 0 && (
        <div className="callout mb-3">
          <strong>Inbox zero.</strong> Nothing overdue, no pending decisions, no expiring COIs, no
          open warranty emergencies. Pour a coffee.
        </div>
      )}

      {/* v2 automation counts row */}
      {data.v2_counts && (
        <div
          className="card mb-3"
          style={{ background: 'rgba(127,163,195,0.04)' }}
        >
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
            <span className="muted small" style={{ marginRight: 'auto' }}>
              <strong>v2 automation queue</strong>
            </span>
            <CountChip label="Inbox" value={data.v2_counts.inbox_unprocessed} />
            <CountChip label="Comms drafts" value={data.v2_counts.client_comms_drafts} />
            <CountChip label="Sub notif drafts" value={data.v2_counts.sub_notifs_drafts} />
            <CountChip label="New leads (7d)" value={data.v2_counts.leads_new_7d} />
            <CountChip label="Scope gaps" value={data.v2_counts.scope_gaps_unresolved} />
            <CountChip label="Spec flags" value={data.v2_counts.spec_flags_open} alert />
            <CountChip label="Weather watch" value={data.v2_counts.weather_watch_days_7d} />
          </div>
        </div>
      )}

      {/* v2 Module 6: per-project weather widget */}
      {data.active_projects.map((p) => {
        const w = weather[p.id]
        if (!w) return null
        const conflicts = w.conflicts ?? []
        return (
          <div key={p.id} className="card mb-3" style={{ background: 'rgba(127,163,195,0.05)' }}>
            <div className="row-between">
              <h3 style={{ margin: 0 }}>
                Weather · {p.name} <span className="muted small">7-day</span>
              </h3>
              {conflicts.length > 0 && (
                <span style={{ color: '#cc4444', fontSize: '0.85rem' }}>
                  ⚠ {conflicts.length} schedule conflict{conflicts.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="row mt-2" style={{ gap: '0.6rem', flexWrap: 'wrap' }}>
              {w.forecast.slice(0, 7).map((f) => (
                <div
                  key={f.forecast_date}
                  style={{
                    minWidth: 110,
                    padding: '6px 8px',
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.04)',
                  }}
                >
                  <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {new Date(f.forecast_date).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '0.85rem' }}>
                    {f.high_temp_f != null ? `${f.high_temp_f}°` : '—'}
                    {f.low_temp_f != null ? ` / ${f.low_temp_f}°` : ''}
                  </div>
                  <div className="muted small" style={{ fontSize: '0.72rem' }}>
                    {f.short_forecast ?? '—'}
                  </div>
                  {f.precipitation_chance != null && f.precipitation_chance > 0 && (
                    <div style={{ fontSize: '0.72rem', color: '#7fa3c3' }}>
                      💧 {f.precipitation_chance}%
                    </div>
                  )}
                </div>
              ))}
            </div>
            {conflicts.length > 0 && (
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                {conflicts.slice(0, 6).map((c, i) => (
                  <li
                    key={i}
                    className="small"
                    style={{
                      color:
                        c.severity === 'critical'
                          ? '#cc4444'
                          : c.severity === 'warning'
                          ? '#d4af37'
                          : 'inherit',
                    }}
                  >
                    {c.forecast_date} — {c.task_name}: {c.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}

      {/* Overdue decisions */}
      {data.overdue_decisions.length > 0 && (
        <Section title={`${data.overdue_decisions.length} overdue decision${data.overdue_decisions.length === 1 ? '' : 's'}`} severity="red">
          {data.overdue_decisions.map((d) => (
            <Row
              key={d.id}
              left={d.decision_name}
              sub={`${d.project_name} · ${d.phase_needed_for ?? '—'}`}
              right={fmtDate(d.deadline)}
              rightSub={
                d.deadline ? `${Math.abs(daysBetween(new Date(), d.deadline) ?? 0)}d late` : ''
              }
              danger
            />
          ))}
        </Section>
      )}

      {/* Soon decisions */}
      {data.soon_decisions.length > 0 && (
        <Section title={`${data.soon_decisions.length} decision${data.soon_decisions.length === 1 ? '' : 's'} due in 14 days`} severity="gold">
          {data.soon_decisions.map((d) => (
            <Row
              key={d.id}
              left={d.decision_name}
              sub={`${d.project_name} · ${d.phase_needed_for ?? '—'}`}
              right={fmtDate(d.deadline)}
              rightSub={d.deadline ? `${daysBetween(new Date(), d.deadline)}d` : ''}
            />
          ))}
        </Section>
      )}

      {/* Overdue selections */}
      {data.overdue_selections.length > 0 && (
        <Section
          title={`${data.overdue_selections.length} selection${data.overdue_selections.length === 1 ? '' : 's'} past deadline`}
          severity="red"
        >
          {data.overdue_selections.map((s) => (
            <Row
              key={s.id}
              left={`${s.category} — ${s.item_description ?? '—'}`}
              sub={s.project_name}
              right={fmtDate(s.decision_deadline)}
              rightSub={s.decision_deadline ? `${Math.abs(daysBetween(new Date(), s.decision_deadline) ?? 0)}d late` : ''}
              danger
            />
          ))}
        </Section>
      )}

      {/* Pending COs */}
      {data.pending_change_orders.length > 0 && (
        <Section
          title={`${data.pending_change_orders.length} change order${data.pending_change_orders.length === 1 ? '' : 's'} awaiting client signature`}
          severity="gold"
        >
          {data.pending_change_orders.map((co) => (
            <Row
              key={co.id}
              left={`CO-${String(co.co_number).padStart(2, '0')} · ${co.description}`}
              sub={co.project_name}
              right={fmtMoney(co.cost_impact)}
              rightSub={`${co.schedule_impact_days}d schedule`}
            />
          ))}
        </Section>
      )}

      {/* Expiring COIs */}
      {data.expiring_cois.length > 0 && (
        <Section title={`${data.expiring_cois.length} COI${data.expiring_cois.length === 1 ? '' : 's'} expiring within 30 days`} severity="gold">
          {data.expiring_cois.map((s) => {
            const days = s.coi_expiration_date ? daysBetween(new Date(), s.coi_expiration_date) : null
            return (
              <Row
                key={s.id}
                left={s.company_name}
                sub={s.contact_name ?? '—'}
                right={s.coi_expiration_date ? fmtDate(s.coi_expiration_date) : '—'}
                rightSub={days !== null ? (days < 0 ? `EXPIRED ${Math.abs(days)}d` : `${days}d`) : ''}
                danger={days !== null && days < 7}
              />
            )
          })}
        </Section>
      )}

      {/* Open warranty (emergencies first) */}
      {data.open_warranty_claims.length > 0 && (
        <Section
          title={`${data.open_warranty_claims.length} open warranty claim${data.open_warranty_claims.length === 1 ? '' : 's'}`}
          severity={data.open_warranty_claims.some((c) => c.is_emergency === 1) ? 'red' : 'gold'}
        >
          {data.open_warranty_claims.map((wc) => (
            <Row
              key={wc.id}
              left={`${wc.is_emergency === 1 ? '⚠ EMERGENCY · ' : ''}${wc.title}`}
              sub={`${wc.project_name} · ${wc.responsible_sub_name ?? 'unassigned'}`}
              right={wc.status.replace('_', ' ').toUpperCase()}
              rightSub={fmtDate(wc.submitted_at)}
              danger={wc.is_emergency === 1}
            />
          ))}
        </Section>
      )}

      {/* Upcoming compliance */}
      {data.upcoming_compliance.length > 0 && (
        <Section title={`${data.upcoming_compliance.length} compliance deadline${data.upcoming_compliance.length === 1 ? '' : 's'} within 30 days`} severity="gold">
          {data.upcoming_compliance.map((c) => (
            <Row
              key={c.id}
              left={c.title}
              sub={`${c.category} · ${c.responsible ?? '—'}`}
              right={fmtDate(c.due_date)}
              rightSub={c.due_date ? `${daysBetween(new Date(), c.due_date)}d` : ''}
            />
          ))}
        </Section>
      )}

      {/* Materials this week */}
      {data.materials_this_week.length > 0 && (
        <Section title={`${data.materials_this_week.length} material${data.materials_this_week.length === 1 ? '' : 's'} arriving this week`} severity="gray">
          {data.materials_this_week.map((m) => (
            <Row
              key={m.id}
              left={m.item}
              sub={`${m.project_name} · ${m.vendor ?? '—'}`}
              right={fmtDate(m.eta_date)}
              rightSub={fmtMoney(m.total_amount)}
            />
          ))}
        </Section>
      )}

      {/* Inspections this week */}
      {data.inspections_this_week.length > 0 && (
        <Section title={`${data.inspections_this_week.length} inspection${data.inspections_this_week.length === 1 ? '' : 's'} this week`} severity="gray">
          {data.inspections_this_week.map((i) => (
            <Row
              key={i.id}
              left={i.inspection_name}
              sub={`${i.project_name} · ${i.type.replace('_', ' ')}`}
              right={fmtDate(i.scheduled_date)}
              rightSub={i.inspector_name ?? '—'}
            />
          ))}
        </Section>
      )}

      {/* Open punch (count only) */}
      {data.open_punch_items.length > 0 && (
        <Section title={`${data.open_punch_items.length} open punch item${data.open_punch_items.length === 1 ? '' : 's'}`} severity="gray">
          {data.open_punch_items.slice(0, 10).map((p) => (
            <Row
              key={p.id}
              left={p.description}
              sub={`${p.project_name} · ${p.responsible_sub_name ?? 'unassigned'}`}
              right={p.status.replace('_', ' ').toUpperCase()}
              rightSub={p.blocks_payment === 1 ? 'BLOCKS PAYMENT' : ''}
              danger={p.blocks_payment === 1}
            />
          ))}
          {data.open_punch_items.length > 10 && (
            <p className="muted small">+ {data.open_punch_items.length - 10} more — see Punch tab.</p>
          )}
        </Section>
      )}
    </section>
  )
}

function CountChip({
  label,
  value,
  alert,
}: {
  label: string
  value: number
  alert?: boolean
}) {
  return (
    <span
      style={{
        background: value > 0 ? (alert ? 'rgba(204,68,68,0.15)' : 'rgba(212,175,55,0.18)') : 'rgba(255,255,255,0.04)',
        color: value > 0 ? 'inherit' : 'var(--muted)',
        fontSize: '0.78rem',
        padding: '3px 8px',
        borderRadius: 3,
      }}
    >
      <strong>{value}</strong> {label}
    </span>
  )
}

function Section({
  title,
  severity,
  children,
}: {
  title: string
  severity: 'red' | 'gold' | 'gray'
  children: React.ReactNode
}) {
  const color = severity === 'red' ? 'var(--danger)' : severity === 'gold' ? 'var(--gold)' : 'var(--muted)'
  return (
    <div className="card mb-3" style={{ borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: color }}>
      <h3 style={{ color }}>{title}</h3>
      {children}
    </div>
  )
}

function Row({
  left,
  sub,
  right,
  rightSub,
  danger,
}: {
  left: string
  sub: string
  right: string | React.ReactNode
  rightSub?: string
  danger?: boolean
}) {
  return (
    <div className="row-list" style={{ cursor: 'default' }}>
      <div className="grow">
        <div className="row-title">{left}</div>
        <div className="muted small">{sub}</div>
      </div>
      <div className="text-right">
        <div className={`row-date ${danger ? 'danger' : ''}`}>{right}</div>
        {rightSub && <div className={`muted small ${danger ? 'danger' : ''}`}>{rightSub}</div>}
      </div>
    </div>
  )
}
